import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { ApiError } from '@/lib/api';
import { useNotify } from '@/stores/notificationStore';
import { formatDateTime } from '@/utils/locale';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import {
  getTestEnvironment,
  refreshTestData,
  type TestEnvironmentState,
} from '@/api/testEnvironment';
import { GithubErrorAlert } from './GithubErrorAlert';

const BLOCK_KEYS = {
  not_configured: 'testData.blocked.notConfigured',
  github_unavailable: 'testData.blocked.githubUnavailable',
  refresh_in_progress: 'testData.blocked.inProgress',
} as const;

/** A run that started after the request (minus clock skew) has finished. */
function finishedAfter(
  state: TestEnvironmentState | undefined,
  requestedAt: number | null
): boolean {
  const run = state?.latestRun;
  if (requestedAt === null || !run) return false;
  if (run.status !== 'success' && run.status !== 'failure') return false;
  return Date.parse(run.startedAt) >= requestedAt - 120_000;
}

function isRunning(state: TestEnvironmentState | undefined): boolean {
  const status = state?.latestRun?.status;
  return status === 'queued' || status === 'in_progress';
}

/**
 * "Hae tuotannon data" (#3462): copies production data into the test
 * environment now instead of at night. Only people holding release.approve
 * may start it; everyone sees when the data was copied.
 *
 * A copy restarts the whole test environment, so the API itself goes away
 * for a while. Polling failures during or right after a copy read as
 * "restarting", never as an error toast.
 */
export function TestDataCard() {
  const { t } = useTranslation('releaseApproval');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  // From the moment this session asks for a copy, or sees one running, until
  // a run that started after that has finished, an unreachable API is the
  // expected restart, not a failure. GitHub may not list the new run yet when
  // the first poll after the request comes back.
  const [requestedAt, setRequestedAt] = useState<number | null>(null);
  const [sawRunning, setSawRunning] = useState(false);

  const query = useQuery({
    queryKey: ['test-environment'],
    queryFn: getTestEnvironment,
    refetchInterval: (q) =>
      requestedAt !== null ||
      sawRunning ||
      isRunning(q.state.data as TestEnvironmentState | undefined)
        ? 10_000
        : 60_000,
    retry: false,
  });

  const running = isRunning(query.data);
  const reachable = query.isSuccess && !query.isError;
  const finishedSinceRequest = finishedAfter(query.data, requestedAt);
  useEffect(() => {
    if (running) setSawRunning(true);
    else if (reachable) setSawRunning(false);
    if (finishedSinceRequest) setRequestedAt(null);
  }, [running, reachable, finishedSinceRequest]);
  const copyUnderway = running || sawRunning || requestedAt !== null;

  const refresh = useMutation({
    mutationFn: refreshTestData,
    onSuccess: () => {
      setDialogOpen(false);
      setRequestedAt(Date.now());
      notify.success(t('testData.requested'));
      void queryClient.invalidateQueries({ queryKey: ['test-environment'] });
    },
    onError: (error) => {
      setDialogOpen(false);
      const code =
        error instanceof ApiError && typeof error.data === 'object' && error.data !== null
          ? (error.data as { code?: string }).code
          : undefined;
      notify.error(
        t(
          code === 'refresh_in_progress'
            ? 'testData.blocked.inProgress'
            : code === 'github_permission_missing'
              ? 'github.permissionMissingUnnamed'
              : 'testData.failed'
        )
      );
      void queryClient.invalidateQueries({ queryKey: ['test-environment'] });
    },
  });

  const state = query.data;
  const holdsPermission = state !== undefined && state.blockedReason !== 'no_permission';
  const names = state?.refreshers.map((r) => r.name).join(', ') ?? '';

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('testData.title')}
        </Typography>
        <Stack spacing={2} alignItems="flex-start">
          {query.isLoading && <CircularProgress size={20} />}

          {query.isError &&
            (copyUnderway ? (
              <Alert
                severity="info"
                icon={<CircularProgress size={18} />}
                sx={{ alignSelf: 'stretch' }}
              >
                {t('testData.restarting')}
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ alignSelf: 'stretch' }}>
                {t('testData.loadFailed')}
              </Alert>
            ))}

          {state && (
            <>
              <Typography data-testid="test-data-copied">
                {state.restore?.restoredAt
                  ? t('testData.copiedAt', {
                      time: formatDateTime(state.restore.restoredAt),
                      backup: state.restore.backupStoppedAt
                        ? formatDateTime(state.restore.backupStoppedAt)
                        : t('unknownVersion'),
                    })
                  : t('testData.neverCopied')}
              </Typography>

              {running && (
                <Alert
                  severity="info"
                  icon={<CircularProgress size={18} />}
                  sx={{ alignSelf: 'stretch' }}
                >
                  {state.latestRun?.requestedBy
                    ? t('testData.runningBy', { name: state.latestRun.requestedBy })
                    : t('testData.running')}
                </Alert>
              )}
              {!running && state.latestRun?.status === 'failure' && (
                <Alert severity="error" sx={{ alignSelf: 'stretch' }}>
                  {t('testData.lastFailed')}
                </Alert>
              )}

              {holdsPermission ? (
                <>
                  {state.blockedReason === 'github_permission_missing' ? (
                    <GithubErrorAlert error={state.githubError} />
                  ) : (
                    state.blockedReason &&
                    state.blockedReason !== 'refresh_in_progress' &&
                    state.blockedReason !== 'no_permission' && (
                      <Alert severity="info" sx={{ alignSelf: 'stretch' }}>
                        {t(BLOCK_KEYS[state.blockedReason])}
                      </Alert>
                    )
                  )}
                  <Button
                    variant="outlined"
                    color="warning"
                    disabled={!state.canRefresh || refresh.isPending}
                    onClick={() => {
                      setDialogOpen(true);
                    }}
                  >
                    {t('testData.button')}
                  </Button>
                </>
              ) : (
                <Typography color="text.secondary" data-testid="test-data-who">
                  {names
                    ? t('testData.whoCan', { names })
                    : state.restore?.restoredAt
                      ? t('noApprovers', { time: formatDateTime(state.restore.restoredAt) })
                      : t('noApproversNoTime')}
                </Typography>
              )}
            </>
          )}
        </Stack>
      </CardContent>

      <ResponsiveDialog
        open={dialogOpen}
        onClose={
          refresh.isPending
            ? undefined
            : () => {
                setDialogOpen(false);
              }
        }
        mobileTitle={t('testData.dialog.title')}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: { xs: 'none', sm: 'block' } }}>
          {t('testData.dialog.title')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">{t('testData.dialog.warning')}</Alert>
            <Typography>{t('testData.dialog.body')}</Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialogOpen(false);
            }}
            disabled={refresh.isPending}
          >
            {tc('cancel')}
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={refresh.isPending}
            onClick={() => {
              refresh.mutate();
            }}
          >
            {t('testData.dialog.confirm')}
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </Card>
  );
}
